import { memo, ReactNode, useCallback, useEffect, useRef } from 'react';
import { config } from 'core/config';
import {
  CardPaymentEntities,
  PaymentError,
  PaymentFormMerchantData,
  PaymentFormParams,
  PaymentInteraction,
  PaymentOrderDescription,
  PixVariant,
} from 'core/payments/entities';
import { PaymentForm, PaymentSubmit } from './types';
import { InvisibleGooglePay, InvisiblePix, InvisiblePixQr, Wrapper } from './styled';

const containerId = 'solid-payment-form-container';

type PaymentCardProps = {
  formMerchantData: PaymentFormMerchantData;
  className?: string;
  params: PaymentFormParams;
  onError?: (data: PaymentError) => void;
  onFailed?: (data: PaymentError) => void;
  onSubmitted?: (type: CardPaymentEntities) => void;
  onSucceed?: (data: { order?: PaymentOrderDescription }) => void;
  onVerify?: () => void;
  onInteraction?: (data: PaymentInteraction) => void;
  shouldRenderPix?: boolean;
  shouldRenderGooglePay?: boolean;
  pixVariant?: PixVariant;
  children?: ReactNode;
};

const PaymentCard = (props: PaymentCardProps) => {
  const {
    className,
    formMerchantData,
    onFailed,
    onSubmitted,
    onSucceed,
    onVerify,
    onError,
    onInteraction,
    params,
    shouldRenderGooglePay = false,
    shouldRenderPix = false,
    pixVariant = PixVariant.AUTOMATICO,
    children,
  } = props;

  const shouldRenderPixAutomatico = shouldRenderPix && pixVariant === PixVariant.AUTOMATICO;
  const shouldRenderPixQr = shouldRenderPix && pixVariant === PixVariant.QR;

  const form = useRef<PaymentForm | null>(null);

  const addFormListeners = useCallback(() => {
    const { current: paymentForm } = form;

    if (!paymentForm) return;

    paymentForm.on('interaction', (event: { data?: PaymentInteraction }) => {
      if (onInteraction && event.data) {
        onInteraction(event.data);
      }
    });
    paymentForm.on('error', (data: { data?: PaymentError } = {}) => {
      const errorData = data.data ? data.data : { message: 'Unknown error' };
      if (onError) {
        onError(errorData);
      }
    });
    paymentForm.on('fail', (data: { data?: PaymentError } = {}) => {
      const errorData = data.data ? data.data : { message: 'Unknown error' };
      if (onFailed) {
        onFailed(errorData);
      }
    });
    paymentForm.on('submit', (e: { data: PaymentSubmit }) => {
      if (onSubmitted) {
        onSubmitted(e.data.entity);
      }
    });
    paymentForm.on('success', (e: { data?: { order?: PaymentOrderDescription } }) => {
      const data = e.data || {};

      if (onSucceed) {
        onSucceed(data);
      }
    });
    paymentForm.on('verify', () => {
      if (onVerify) {
        onVerify();
      }
    });
  }, [onError, onFailed, onSubmitted, onSucceed, onVerify, onInteraction, form]);

  useEffect(() => {
    if (!form.current && !!formMerchantData.paymentForm.merchant && window.PaymentFormSdk) {
      form.current = window.PaymentFormSdk.init({
        merchantData: formMerchantData.paymentForm,
        iframeParams: {
          containerId,
        },
        ...params,
        applePayButtonParams: {
          containerId: config.applePayContainerId,
          integrationType: 'js',
          ...params.applePayButtonParams,
        },
        googlePayButtonParams: {
          containerId: config.googlePayContainerId,
          type: 'plain',
          ...params.googlePayButtonParams,
        },
        pixAutomaticoButtonParams: {
          enabled: shouldRenderPixAutomatico,
          containerId: config.pixAutomaticoContainerId,
          ...params.pixAutomaticoButtonParams,
        },
        pixQrButtonParams: {
          enabled: shouldRenderPixQr,
          containerId: config.pixQrContainerId,
          ...params.pixQrButtonParams,
        },
      });
    }

    addFormListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addFormListeners, formMerchantData, form]);

  useEffect(() => {
    return () => {
      if (form.current) {
        form.current.unsubscribeAll();
      }
    };
  }, []);

  return (
    <>
      <Wrapper className={className} id={containerId}>
        {children}
      </Wrapper>
      {!shouldRenderPixAutomatico && <InvisiblePix id={config.pixAutomaticoContainerId} />}
      {!shouldRenderPixQr && <InvisiblePixQr id={config.pixQrContainerId} />}
      {!shouldRenderGooglePay && <InvisibleGooglePay id={config.googlePayContainerId} />}
    </>
  );
};

export default memo(PaymentCard);
